import yaml from 'js-yaml';

/**
 * Main YAML validation function
 * @param {string} yamlString - The YAML string to validate
 * @param {string} configKey - The configuration key (eventTypes, conditions, actions, etc.)
 * @returns {string|null} - Error message if invalid, null if valid
 */
export const validateYaml = (yamlString, configKey) => {
  try {
    // Check if empty
    if (!yamlString || !yamlString.trim()) {
      return 'Configuration cannot be empty';
    }

    // Use proper YAML parser for validation
    const parsed = yaml.load(yamlString, {
      // Safe loading to prevent code execution
      schema: yaml.SAFE_SCHEMA,
      // Allow duplicate keys but warn about them
      onWarning: (warning) => {
        console.warn(`YAML Warning in ${configKey}:`, warning.message);
      }
    });

    // Check if parsed result is valid
    if (parsed === null || parsed === undefined) {
      return 'YAML content parsed to null or undefined. Please check your syntax.';
    }

    // Validate structure based on config type
    const validationResult = validateConfigStructure(parsed, configKey);
    if (validationResult) {
      return validationResult;
    }

    return null; // Valid YAML
  } catch (error) {
    // Handle specific YAML parsing errors
    if (error.name === 'YAMLException') {
      const lineInfo = error.mark ? ` (Line ${error.mark.line + 1}, Column ${error.mark.column + 1})` : '';
      return `YAML Syntax Error${lineInfo}: ${error.reason || error.message}`;
    }
    
    return `Invalid YAML format: ${error.message}`;
  }
};

/**
 * Validates configuration structure based on type
 * @param {*} parsed - Parsed YAML object
 * @param {string} configKey - Configuration key
 * @returns {string|null} - Error message if invalid, null if valid
 */
export const validateConfigStructure = (parsed, configKey) => {
  try {
    // Validate structure based on configuration type
    switch (configKey) {
      case 'eventTypes':
        return validateEventTypesStructure(parsed);
      case 'conditions':
        return validateConditionsStructure(parsed);
      case 'actions':
        return validateActionsStructure(parsed);
      case 'eventAcl':
      case 'actionAcl':
        return validateAclStructure(parsed);
      default:
        // For unknown config types, just ensure it's an object
        if (typeof parsed !== 'object' || parsed === null) {
          return `Configuration must be a valid object structure`;
        }
        return null;
    }
  } catch (error) {
    return `Structure validation error: ${error.message}`;
  }
};

/**
 * Validates event types structure
 * @param {*} parsed - Parsed YAML object
 * @returns {string|null} - Error message if invalid, null if valid
 */
export const validateEventTypesStructure = (parsed) => {
  if (typeof parsed !== 'object' || parsed === null) {
    return 'Event types must be an object with event definitions';
  }

  // Check that each event type has required fields
  for (const [eventName, eventConfig] of Object.entries(parsed)) {
    if (typeof eventConfig !== 'object' || eventConfig === null) {
      return `Event type "${eventName}" must be a valid object`;
    }
    
    if (!eventConfig.actions || !Array.isArray(eventConfig.actions)) {
      return `Event type "${eventName}" must have an "actions" array`;
    }

    if (typeof eventConfig.deprecated !== 'boolean') {
      return `Event type "${eventName}" must have a "deprecated" boolean field`;
    }
  }

  return null;
};

/**
 * Validates conditions structure
 * @param {*} parsed - Parsed YAML object
 * @returns {string|null} - Error message if invalid, null if valid
 */
export const validateConditionsStructure = (parsed) => {
  if (typeof parsed !== 'object' || parsed === null) {
    return 'Conditions must be an object with condition definitions';
  }

  // Check that each condition has required fields
  for (const [conditionName, conditionConfig] of Object.entries(parsed)) {
    if (typeof conditionConfig !== 'object' || conditionConfig === null) {
      return `Condition "${conditionName}" must be a valid object`;
    }

    if (!conditionConfig.allowedOperators || typeof conditionConfig.allowedOperators !== 'string') {
      return `Condition "${conditionName}" must have an "allowedOperators" string`;
    }

    if (!conditionConfig.objectType || typeof conditionConfig.objectType !== 'string') {
      return `Condition "${conditionName}" must have an "objectType" string`;
    }
  }

  return null;
};

/**
 * Validates actions structure
 * @param {*} parsed - Parsed YAML object
 * @returns {string|null} - Error message if invalid, null if valid
 */
export const validateActionsStructure = (parsed) => {
  if (typeof parsed !== 'object' || parsed === null) {
    return 'Actions must be a valid object with action definitions';
  }

  // Check that each action has required fields
  for (const [actionName, actionConfig] of Object.entries(parsed)) {
    if (typeof actionConfig !== 'object' || actionConfig === null) {
      return `Action "${actionName}" must be a valid object`;
    }

    if (!actionConfig.description || typeof actionConfig.description !== 'string') {
      return `Action "${actionName}" must have a "description" string`;
    }
  }

  return null;
};

/**
 * Validates ACL structure
 * @param {*} parsed - Parsed YAML object
 * @returns {string|null} - Error message if invalid, null if valid
 */
export const validateAclStructure = (parsed) => {
  if (typeof parsed !== 'object' || parsed === null) {
    return 'ACL configuration must be a valid object';
  }

  // ACL structure is more flexible, just ensure it's a valid object
  return null;
};

/**
 * Validates cross-references between all configurations
 * @param {object} configs - Object containing all parsed configurations
 * @returns {string|null} - Error message if invalid, null if valid
 */
export const validateCrossReferences = (configs) => {
  const errors = [];

  try {
    const eventTypes = configs.eventTypes || {};
    const conditions = configs.conditions || {};
    const actions = configs.actions || {};
    const eventAcl = configs.eventAcl || {};
    const actionAcl = configs.actionAcl || {};

    // 1. Validate ruleTypes in events config exist in conditions config
    for (const [eventName, eventConfig] of Object.entries(eventTypes)) {
      if (eventConfig && eventConfig.ruleTypes && Array.isArray(eventConfig.ruleTypes)) {
        for (const ruleType of eventConfig.ruleTypes) {
          if (!conditions[ruleType]) {
            errors.push(`Event "${eventName}" references ruleType "${ruleType}" which is not defined in conditions config`);
          }
        }
      }
    }

    // 2. Validate actions in events config exist in actions config
    for (const [eventName, eventConfig] of Object.entries(eventTypes)) {
      if (eventConfig && eventConfig.actions && Array.isArray(eventConfig.actions)) {
        for (const actionName of eventConfig.actions) {
          if (!actions[actionName]) {
            errors.push(`Event "${eventName}" references action "${actionName}" which is not defined in actions config`);
          }
        }
      }
    }

    // 3. Validate eventName in eventAcl config exists in events config
    for (const eventName of Object.keys(eventAcl)) {
      if (!eventTypes[eventName]) {
        errors.push(`Event ACL references event "${eventName}" which is not defined in events config`);
      }
    }

    // 4. Validate actionName in actionAcl config exists in actions config
    for (const actionName of Object.keys(actionAcl)) {
      if (!actions[actionName]) {
        errors.push(`Action ACL references action "${actionName}" which is not defined in actions config`);
      }
    }

    return errors.length > 0 ? errors.join('; ') : null;
  } catch (error) {
    return `Cross-reference validation error: ${error.message}`;
  }
};

/**
 * Enhanced YAML validation that includes cross-reference validation
 * @param {object} allConfigs - Object containing all configuration strings
 * @returns {object} - Object with validation results for each config
 */
export const validateAllConfigs = (allConfigs) => {
  const results = {};
  const parsedConfigs = {};

  // First, validate individual YAML syntax and structure for each config
  for (const [configKey, yamlString] of Object.entries(allConfigs)) {
    const validationResult = validateYaml(yamlString, configKey);
    results[configKey] = validationResult;

    // If individual validation passes, parse and store for cross-reference validation
    if (!validationResult) {
      try {
        parsedConfigs[configKey] = yaml.load(yamlString, { schema: yaml.SAFE_SCHEMA });
      } catch (error) {
        results[configKey] = `Failed to parse ${configKey}: ${error.message}`;
      }
    }
  }

  // If all individual validations pass, perform cross-reference validation
  const hasIndividualErrors = Object.values(results).some(result => result !== null);
  if (!hasIndividualErrors) {
    const crossRefError = validateCrossReferences(parsedConfigs);
    if (crossRefError) {
      // Add cross-reference error to a special key
      results._crossReference = crossRefError;
    }
  }

  return results;
};
